
import { supabase } from '@/integrations/supabase/client';
import { UserRole } from '@/types/user-roles';

// Network connectivity check
const checkNetworkConnectivity = async (): Promise<boolean> => {
  try {
    // Simple ping to Supabase to check connectivity
    const { error } = await supabase.from('admins').select('count').limit(1);
    return !error;
  } catch {
    return false;
  }
};

// Enhanced error classification
const classifyError = (error: any): { type: 'network' | 'auth' | 'database' | 'unknown'; retryable: boolean } => {
  const message = error?.message?.toLowerCase() || '';
  
  if (message.includes('load failed') || message.includes('fetch') || message.includes('network')) {
    return { type: 'network', retryable: true };
  }
  
  if (message.includes('jwt') || message.includes('unauthorized') || message.includes('forbidden')) {
    return { type: 'auth', retryable: false };
  }
  
  if (message.includes('relation') || message.includes('column') || message.includes('syntax')) {
    return { type: 'database', retryable: false };
  }
  
  return { type: 'unknown', retryable: true };
};

/**
 * Checks if a user has super admin privileges by querying the admins table
 * @param userId The ID of the user to check
 * @returns Promise<boolean> True if the user is a super admin, false otherwise
 */
export async function isSuperAdmin(userId: string): Promise<boolean> {
  try {
    console.log('Checking super admin status for user:', userId);
    
    // First check network connectivity
    const isConnected = await checkNetworkConnectivity();
    if (!isConnected) {
      throw new Error('Network connectivity check failed - please check your internet connection');
    }
    
    // Query with optimized settings
    const { data, error } = await supabase
      .from('admins')
      .select('role')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      console.error('Error checking admin status:', error);
      const errorInfo = classifyError(error);
      
      // Throw appropriate error message based on error type
      switch (errorInfo.type) {
        case 'network':
          throw new Error(`Network error: ${error.message}. Please check your connection and try again.`);
        case 'auth':
          throw new Error(`Authentication error: ${error.message}. Please log in again.`);
        case 'database':
          throw new Error(`Database error: ${error.message}. Please contact support.`);
        default:
          throw new Error(`Admin verification failed: ${error.message}`);
      }
    }

    const result = data?.role === 'super_admin';
    console.log('Super admin check result:', result, 'for role:', data?.role);
    return result;
  } catch (error) {
    console.error('Exception checking admin status:', error);
    // Re-throw with enhanced error information
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Unexpected error during admin verification');
  }
}

/**
 * Gets the admin role for a user from the admins table
 * @param userId The ID of the user to check
 * @returns Promise<UserRole | null> The user's admin role or null if not found
 */
export async function getUserAdminRole(userId: string): Promise<UserRole | null> {
  try {
    console.log('Getting admin role for user:', userId);
    
    // Check network connectivity first
    const isConnected = await checkNetworkConnectivity();
    if (!isConnected) {
      throw new Error('Network connectivity check failed');
    }
    
    const { data, error } = await supabase
      .from('admins')
      .select('role')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      console.error('Error getting admin role:', error);
      const errorInfo = classifyError(error);
      throw new Error(`${errorInfo.type} error: ${error.message}`);
    }

    const role = data?.role as UserRole || null;
    console.log('Admin role result:', role);
    return role;
  } catch (error) {
    console.error('Exception getting admin role:', error);
    throw error;
  }
}

/**
 * Diagnostic function to check admin table accessibility
 * @returns Promise<{accessible: boolean, error?: string, tableExists: boolean}>
 */
export async function diagnoseAdminAccess(): Promise<{
  accessible: boolean;
  error?: string;
  tableExists: boolean;
  networkOk: boolean;
}> {
  const result = {
    accessible: false,
    tableExists: false,
    networkOk: false
  };
  
  try {
    // Check network first
    result.networkOk = await checkNetworkConnectivity();
    
    if (!result.networkOk) {
      return { ...result, error: 'Network connectivity failed' };
    }
    
    // Check if admins table exists and is accessible
    const { data, error } = await supabase
      .from('admins')
      .select('count')
      .limit(1);
    
    if (error) {
      return { ...result, error: error.message };
    }
    
    result.tableExists = true;
    result.accessible = true;
    
    return result;
  } catch (error) {
    return { 
      ...result, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}
